import React, { FC, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import Checkbox from '@cld/checkbox';
import { InlineFieldLayout, Label } from '@cld/form';
import { globalTheme } from '@cld/component-theme';
import TextField from '@cld/text-field';
import { typography } from '@cld/typography';
import { updateOptions } from '../../../../actions';
import { messages } from '../../../../i18n';
import { COOKBOOK_URL } from '../../../../constants/general';
import { useOptions } from '../../../../selectors';
import { useAnalytics } from '../../../../hooks/useAnalytics';

const Link = styled.a`
  display: block;
  margin-top: ${globalTheme.spacing.xs};
  ${typography.xxs.url};
`;

const CustomTransformationTextFieldWrapper = styled.div`
  margin-top: ${globalTheme.spacing.sm};
`;

export const TransformationsCustomSetup: FC = () => {
  const analytics = useAnalytics();

  const { formatMessage } = useIntl();
  const { areCustomTransformationsEnabled, customTransformations } = useOptions();
  const [localCustomTransformations, setLocalCustomTransformations] = useState(customTransformations);
  const dispatch = useDispatch();

  const handleHasCustomTransformationsChange = () => {
    analytics('edit custom transformations', !areCustomTransformationsEnabled ? 'check' : 'uncheck');
    dispatch(updateOptions({ areCustomTransformationsEnabled: !areCustomTransformationsEnabled }));
  };
  const handleUpdateTransformations = useCallback(() => {
    dispatch(updateOptions({ customTransformations: localCustomTransformations }));
  }, [localCustomTransformations, dispatch]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleUpdateTransformations();
    }
  };

  useEffect(() => {
    setLocalCustomTransformations(customTransformations);
  }, [customTransformations]);

  return (
    <>
      <InlineFieldLayout
        label={
          <Label htmlFor="has-custom-transformations">
            <FormattedMessage {...messages['demo.section.transformation.custom.label']} />
          </Label>
        }
      >
        <Checkbox value={areCustomTransformationsEnabled} onClick={handleHasCustomTransformationsChange} id="has-custom-transformations" />
      </InlineFieldLayout>
      {areCustomTransformationsEnabled && (
        <CustomTransformationTextFieldWrapper data-test="custom-transformations-textfield">
          <TextField
            type="text"
            placeholder={`w_90,r_max ${formatMessage(messages['demo.section.transformation.custom.placeholder'])}`}
            value={localCustomTransformations}
            onChange={setLocalCustomTransformations}
            inputProps={{ onKeyDown: handleKeyDown }}
          />
          <Link href={COOKBOOK_URL} target="_blank" rel="noopener noreferrer">
            <FormattedMessage {...messages['demo.section.transformation.infoMessage']} />
          </Link>
        </CustomTransformationTextFieldWrapper>
      )}
    </>
  );
};
